import { View } from '@react-pdf/renderer';
import { PricingModelTab, Supplier } from '../../../common';
import { CreditNote } from '../../../credit/interfaces/credit-note-interface';
import { InvoiceCredit } from '../../../credit/interfaces/invoice-credit.interface';
import { getTaxValue, getTotalValue } from '../../../pricing';
import { en } from '../../theme/languages/en';
import {
  sum,
  getCreditBalance,
  creditNoteSummaryValuesList,
  getPaidCredits,
  getConnectedInvoiceLabel,
  getCreditNoteIdName,
} from '../../utils';
import { CreditNoteMetaData } from '../CreditNoteMetaData/CreditNoteMetaData';
import { Span } from '../Span/Span';
import { SummaryData } from '../SummaryData/SummaryData';
import { SummaryProductTable } from '../SummaryProductTable/SummaryProductTable';
import { SummaryTable } from '../SummaryTable/SummaryTable';
import { EntityBankData } from '../EntityBankData/EntityBankData';
import { styles } from '../../pages/CreditNotePDF/CreditNotePDF.module';

interface CreditNoteContentProps {
  creditNoteData: CreditNote;
  invoiceCredit?: InvoiceCredit;
  supplier: Supplier;
}

export const CreditNoteContent = ({ creditNoteData, invoiceCredit, supplier }: CreditNoteContentProps) => {
  const taxValue = sum(
    creditNoteData?.pricingModel?.tabs?.map(
      (tab: PricingModelTab) => getTaxValue(tab?.pricingModelTableTotal, 0, tab?.tax) || 0,
    ) || [],
  );
  const totalValue = sum(
    creditNoteData?.pricingModel?.tabs?.map(
      (tab: PricingModelTab) => getTotalValue(tab?.pricingModelTableTotal, 0, tab?.tax) || 0,
    ) || [],
  );
  const subTotalValue = sum(
    creditNoteData?.pricingModel?.tabs?.map((tab: PricingModelTab) => tab?.pricingModelTableTotal || 0) || [],
  );

  const supplierData = [
    { key: 'companyName', value: supplier?.legalName },
    { key: 'companyEIN', value: supplier?.ein },
    { key: 'registeredAddress', value: supplier?.shippingAddress },
    { key: 'contactInformation', value: supplier?.email },
  ];

  return (
    <View style={styles.container} wrap>
      <CreditNoteMetaData
        creditNoteData={creditNoteData}
        title={`${en.creditNote} ${getCreditNoteIdName(creditNoteData)}`}
      />

      {creditNoteData?.pricingModel?.tabs?.length > 1 ? (
        <SummaryTable products={creditNoteData?.pricingModel?.tabs} periodColumnName={en.creditPeriod} />
      ) : (
        <SummaryProductTable
          product={creditNoteData?.pricingModel?.tabs[0]}
          isPartialPeriod={creditNoteData?.pricingModel?.tabs[0]?.isPartialPeriod}
          style={styles.productTable}
          showOnlyTable
        />
      )}

      <SummaryData
        summaryItems={creditNoteSummaryValuesList({
          hidePercentage: creditNoteData?.pricingModel?.tabs?.length > 1,

          subTotal: subTotalValue,
          currency: creditNoteData?.pricingModel?.tabs[0]?.currency,
          taxPercentage:
            creditNoteData?.pricingModel?.tabs?.length == 1 ? creditNoteData?.pricingModel?.tabs[0]?.tax : undefined,
          taxCalculatedValue: taxValue,

          creditIsUsed: creditNoteData?.invoiceCredits?.length > 0 || !!invoiceCredit,
          isInvoiceShown: !!invoiceCredit,
          total: totalValue,
          previousCreditsPaid:
            invoiceCredit?.previouslyUsedCredit ||
            sum(creditNoteData?.invoiceCredits?.map((invoiceCredit) => invoiceCredit?.usedCredit)),
          currentCreditsPaid: getPaidCredits(invoiceCredit, creditNoteData),
          creditBalance: getCreditBalance(totalValue, invoiceCredit, creditNoteData),
          isPartialPeriod: creditNoteData?.pricingModel?.tabs.some((tab) => tab.isPartialPeriod),
        })}
      />
      {creditNoteData?.pricingModel?.tabs?.length == 1 && (
        <View style={styles.noteContainer} wrap={false}>
          <Span style={styles.note} canBeMissingValue missingValue={creditNoteData?.pricingModel?.tabs[0]?.note}>
            {en.note}
          </Span>
          <Span style={styles.text} canBeMissingValue missingValue={creditNoteData?.pricingModel?.tabs[0]?.note}>
            {creditNoteData?.pricingModel?.tabs[0]?.note}
          </Span>

          <Span style={styles.text} canBeMissingValue missingValue={creditNoteData?.pricingModel?.tabs[0]?.note}>
            {creditNoteData?.pricingModel?.tabs[0]?.note}
          </Span>
        </View>
      )}

      {!creditNoteData?.isDraft &&
        creditNoteData?.invoiceCredits?.map((invoiceCredit, index) => (
          <Span key={index} style={styles.text}>
            {getConnectedInvoiceLabel(invoiceCredit.invoice)}
          </Span>
        ))}
      <View style={{ marginBottom: 20 }}>
        {!!supplier && <EntityBankData title={en.entityDetails} data={supplierData} />}
      </View>
    </View>
  );
};
